import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Loader2, Edit, Trash2, Ban, CalendarIcon, AlertTriangle, Building2, MapPin, Package } from 'lucide-react';
import { format, parseISO, isBefore, isAfter, startOfDay } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BLACKOUT_TYPES = [
  { value: 'full_property', label: 'Entire Property', icon: Building2, description: 'Block all areas and inventory' },
  { value: 'area', label: 'Specific Area', icon: MapPin, description: 'Block a specific area only' },
  { value: 'inventory_type', label: 'Inventory Type', icon: Package, description: 'Block a specific type (e.g., all Cabanas)' },
];

const COMMON_REASONS = [
  'Private Event',
  'Holiday Closure',
  'Maintenance',
  'Weather Preparation',
  'Staff Training',
  'Renovation',
  'VIP Reservation',
  'Other'
];

export default function BlackoutsManager() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [areas, setAreas] = useState([]);
  const [inventoryTypes, setInventoryTypes] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBlackout, setEditingBlackout] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    reason: '',
    blackout_type: 'full_property',
    target_id: '',
    start_date: new Date(),
    end_date: new Date(),
    all_day: true,
    start_time: '09:00',
    end_time: '17:00',
    notify_guests: true
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchBlackouts();
      fetchAreas();
      fetchInventoryTypes();
    }
  }, [selectedProperty, showPast]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`${API_URL}/areas?property_id=${selectedProperty}`);
      setAreas(response.data);
    } catch (error) {
      console.error('Failed to load areas');
    }
  };

  const fetchInventoryTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory-types?property_id=${selectedProperty}`);
      const allTypes = [
        ...(response.data.default_types || []),
        ...(response.data.custom_types || [])
      ];
      setInventoryTypes(allTypes);
    } catch (error) {
      console.error('Failed to load inventory types');
    }
  };

  const fetchBlackouts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/blackouts?property_id=${selectedProperty}&include_past=${showPast}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBlackouts(response.data);
    } catch (error) {
      toast.error('Failed to load blackouts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        property_id: selectedProperty,
        name: formData.name,
        reason: formData.reason,
        blackout_type: formData.blackout_type,
        target_id: formData.blackout_type !== 'full_property' ? formData.target_id : null,
        start_date: formData.start_date.toISOString(),
        end_date: formData.end_date.toISOString(),
        all_day: formData.all_day,
        start_time: !formData.all_day ? formData.start_time : null,
        end_time: !formData.all_day ? formData.end_time : null,
        notify_guests: formData.notify_guests
      };

      if (editingBlackout) {
        await axios.put(`${API_URL}/blackouts/${editingBlackout.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blackout updated successfully');
      } else {
        await axios.post(`${API_URL}/blackouts`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blackout created successfully');
      }

      setDialogOpen(false);
      setEditingBlackout(null);
      resetForm();
      fetchBlackouts();
    } catch (error) {
      toast.error('Failed to save blackout');
    }
  };

  const handleEdit = (blackout) => {
    setEditingBlackout(blackout);
    setFormData({
      name: blackout.name,
      reason: blackout.reason || '',
      blackout_type: blackout.blackout_type,
      target_id: blackout.target_id || '',
      start_date: parseISO(blackout.start_date),
      end_date: parseISO(blackout.end_date),
      all_day: blackout.all_day,
      start_time: blackout.start_time || '09:00',
      end_time: blackout.end_time || '17:00',
      notify_guests: blackout.notify_guests
    });
    setDialogOpen(true);
  };

  const handleDelete = async (blackoutId) => {
    if (!window.confirm('Are you sure you want to delete this blackout?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/blackouts/${blackoutId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blackout deleted');
      fetchBlackouts();
    } catch (error) {
      toast.error('Failed to delete blackout');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      reason: '',
      blackout_type: 'full_property',
      target_id: '',
      start_date: new Date(),
      end_date: new Date(),
      all_day: true,
      start_time: '09:00',
      end_time: '17:00',
      notify_guests: true
    });
  };

  const openNewDialog = () => {
    setEditingBlackout(null);
    resetForm();
    setDialogOpen(true);
  };

  const getBlackoutStatus = (blackout) => {
    const now = new Date();
    const start = parseISO(blackout.start_date);
    const end = parseISO(blackout.end_date);
    
    if (isBefore(end, startOfDay(now))) {
      return { label: 'Expired', color: 'bg-gray-100 text-gray-600' };
    } else if (isAfter(start, now)) {
      return { label: 'Upcoming', color: 'bg-blue-100 text-blue-800' };
    } else {
      return { label: 'Active', color: 'bg-red-100 text-red-800' };
    }
  };

  const getTargetLabel = (blackout) => {
    if (blackout.blackout_type === 'full_property') {
      return 'Entire Property';
    } else if (blackout.blackout_type === 'area') {
      const area = areas.find(a => a.id === blackout.target_id);
      return area ? area.name : 'Unknown Area';
    } else if (blackout.blackout_type === 'inventory_type') {
      const type = inventoryTypes.find(t => t.slug === blackout.target_id);
      return type ? type.name : blackout.target_id;
    }
    return 'Unknown';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'full_property': return <Building2 className="w-4 h-4" />;
      case 'area': return <MapPin className="w-4 h-4" />;
      case 'inventory_type': return <Package className="w-4 h-4" />;
      default: return <Ban className="w-4 h-4" />;
    }
  };

  return (
    <AdminLayout title="Blackout Management">
      {/* Controls */}
      <div className="mb-6 flex flex-wrap gap-4 items-end justify-between">
        <div className="flex gap-4 items-end">
          <div className="w-64">
            <Label htmlFor="property">Property</Label>
            <select
              id="property"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full mt-2 p-3 border border-[#E7E5E4] rounded-sm"
              data-testid="select-property"
            >
              {properties.map(prop => (
                <option key={prop.id} value={prop.id}>{prop.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showPast"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="showPast" className="cursor-pointer">Show past blackouts</Label>
          </div>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNewDialog}
              className="bg-[#9F1239] hover:bg-[#881337] text-white"
              data-testid="btn-add-blackout"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Blackout
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif">
                {editingBlackout ? 'Edit Blackout' : 'Create Blackout'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Blackout Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Independence Day Closure"
                  required
                  data-testid="input-name"
                />
              </div>

              <div>
                <Label htmlFor="reason">Reason</Label>
                <div className="flex flex-wrap gap-2 mt-2 mb-2">
                  {COMMON_REASONS.map(reason => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setFormData({ ...formData, reason })}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        formData.reason === reason
                          ? 'bg-[#9F1239] text-white border-[#9F1239]'
                          : 'bg-white text-[#78716C] border-[#E7E5E4] hover:border-[#9F1239]'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <Textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Additional details about this blackout..."
                  rows={2}
                  data-testid="input-reason"
                />
              </div>

              <div>
                <Label>Blackout Scope *</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {BLACKOUT_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, blackout_type: type.value, target_id: '' })}
                      className={`p-3 rounded-sm border-2 text-left transition-all ${
                        formData.blackout_type === type.value
                          ? 'border-[#9F1239] bg-[#FEF2F2]'
                          : 'border-[#E7E5E4] hover:border-[#9F1239]'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 mb-1 ${formData.blackout_type === type.value ? 'text-[#9F1239]' : 'text-[#78716C]'}`} />
                      <p className={`text-sm font-medium ${formData.blackout_type === type.value ? 'text-[#9F1239]' : 'text-[#1C1917]'}`}>
                        {type.label}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {formData.blackout_type === 'area' && (
                <div>
                  <Label htmlFor="target_area">Select Area *</Label>
                  <select
                    id="target_area"
                    value={formData.target_id}
                    onChange={(e) => setFormData({ ...formData, target_id: e.target.value })}
                    className="w-full mt-2 p-2 border border-[#E7E5E4] rounded-sm"
                    required
                    data-testid="select-target-area"
                  >
                    <option value="">Select an area...</option>
                    {areas.map(area => (
                      <option key={area.id} value={area.id}>{area.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.blackout_type === 'inventory_type' && (
                <div>
                  <Label htmlFor="target_type">Select Inventory Type *</Label>
                  <select
                    id="target_type"
                    value={formData.target_id}
                    onChange={(e) => setFormData({ ...formData, target_id: e.target.value })}
                    className="w-full mt-2 p-2 border border-[#E7E5E4] rounded-sm"
                    required
                    data-testid="select-target-type"
                  >
                    <option value="">Select a type...</option>
                    {inventoryTypes.map(type => (
                      <option key={type.slug} value={type.slug}>{type.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-2 justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.start_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => date && setFormData({ ...formData, start_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-2 justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.end_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => date && setFormData({ ...formData, end_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="all_day"
                    checked={formData.all_day}
                    onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="all_day" className="cursor-pointer">All day</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="notify_guests"
                    checked={formData.notify_guests}
                    onChange={(e) => setFormData({ ...formData, notify_guests: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="notify_guests" className="cursor-pointer">Notify affected guests</Label>
                </div>
              </div>

              {!formData.all_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_time">Start Time</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_time">End Time</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#9F1239] hover:bg-[#881337]"
                data-testid="btn-submit"
              >
                {editingBlackout ? 'Update Blackout' : 'Create Blackout'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Blackouts List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#9F1239]" />
        </div>
      ) : (
        <div className="space-y-4">
          {blackouts.length === 0 ? (
            <Card className="bg-white border-slate-200 shadow-sm rounded-md">
              <CardContent className="py-12 text-center">
                <Ban className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#78716C]">No blackouts scheduled.</p>
                <p className="text-sm text-[#A8A29E] mt-2">
                  Create blackouts to block reservations during holidays, events, or maintenance.
                </p>
              </CardContent>
            </Card>
          ) : (
            blackouts.map((blackout) => {
              const status = getBlackoutStatus(blackout);
              return (
                <Card 
                  key={blackout.id} 
                  className={`bg-white border-slate-200 shadow-sm rounded-md ${status.label === 'Expired' ? 'opacity-60' : ''}`}
                  data-testid={`blackout-${blackout.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            status.label === 'Active' ? 'bg-red-100' : 'bg-[#F5F5F4]'
                          }`}>
                            {getTypeIcon(blackout.blackout_type)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-serif font-medium text-[#1C1917]">{blackout.name}</h3>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.color}`}>
                                {status.label}
                              </span>
                            </div>
                            <p className="text-sm text-[#78716C]">{getTargetLabel(blackout)}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                          <div>
                            <p className="text-[#78716C]">Start Date</p>
                            <p className="font-medium">{format(parseISO(blackout.start_date), 'MMM d, yyyy')}</p>
                          </div>
                          <div>
                            <p className="text-[#78716C]">End Date</p>
                            <p className="font-medium">{format(parseISO(blackout.end_date), 'MMM d, yyyy')}</p>
                          </div>
                          <div>
                            <p className="text-[#78716C]">Time</p>
                            <p className="font-medium">
                              {blackout.all_day ? 'All Day' : `${blackout.start_time} - ${blackout.end_time}`}
                            </p>
                          </div>
                          {blackout.reason && (
                            <div>
                              <p className="text-[#78716C]">Reason</p>
                              <p className="font-medium">{blackout.reason}</p>
                            </div>
                          )}
                        </div>

                        {blackout.notify_guests && status.label !== 'Expired' && (
                          <div className="flex items-center gap-2 mt-4 text-sm text-[#9F1239]">
                            <AlertTriangle className="w-4 h-4" />
                            <span>Affected guests will be notified</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(blackout)}
                          data-testid={`btn-edit-${blackout.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(blackout.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`btn-delete-${blackout.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-[#FEF2F2] border-[#FECACA] shadow-sm rounded-md mt-8">
        <CardContent className="p-6">
          <h4 className="font-serif font-medium text-[#9F1239] mb-3">How Blackouts Work</h4>
          <ul className="text-sm text-[#78716C] space-y-2">
            <li>• <strong>Entire Property:</strong> Blocks all reservations across all areas</li>
            <li>• <strong>Specific Area:</strong> Only blocks reservations for the selected area (e.g., Beach only)</li>
            <li>• <strong>Inventory Type:</strong> Blocks all items of a specific type (e.g., all Cabanas)</li>
            <li>• Guests with existing reservations during blackout periods will be notified automatically</li>
            <li>• Blackouts prevent new bookings but don't automatically cancel existing ones</li>
          </ul>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
